import type { ActionsNetwork } from 'shared/store/actions'
import { fetchPageData, updateToken } from 'shared/store/actions'
import endpoints from 'shared/store/endpoints'
import { ws } from 'shared/store/ws'
import type { LoginForm, LoginPage, SocialData } from 'shared/types'
import type { Dispatch } from 'redux'
import { errors, messages } from 'shared/utils'
import type { RootState } from 'shared/store/reducers'
import { getWebsocket, sendToken } from 'shared/store/websocket'
import { getAnalyticsData } from 'shared/store/user'
import { updateDeliveryRestaurant } from 'shared/store/ecommerce/order'

type GetState = () => RootState

export type ActionSigninPageDataResponse = {
    type: 'PAGE_DATA_SIGNIN_RESPONSE',
    data: LoginPage
}

type ActionSigninSendFormError = {
    type: 'SIGNIN_SEND_FORM_ERROR',
    login?: string,
    sendFormError: any
}

type ActionSigninResendMailError = {
    type: 'SIGNIN_RESEND_MAIL_ERROR',
    resendMailError: any
}

type ActionSigninResendMailSuccess = {
    type: 'SIGNIN_RESEND_MAIL_SUCCESS'
}

export type ActionSigninSendFormSuccess = {
    type: 'SIGNIN_SEND_FORM_SUCCESS',
    token: string,
    loginType: 'FACEBOOK' | 'GOOGLE' | 'CLASSIC' | 'APPLE',
    fromEcommerce?: boolean
}

type ActionSigninLogout = {
    type: 'LOGOUT'
}

type ActionSigninReset = {
    type: 'SIGNIN_RESET'
}

type ActionSignupSocialPending = {
    type: 'SOCIAL_PENDING',
    data: SocialData
}

type ActionMfaRequired = {
    type: 'SIGNIN_MFA_REQUIRED',
    mfaToken: string
}

export type ActionSigninWithTouchID = {
    type: 'SIGNIN_WITH_TOUCH_ID',
    token: string
}

export type ActionSigninDone = {
    type: 'SIGNIN_DONE'
}

export type ActionSetSigninRedirect = {
    type: 'SIGNIN_SET_REDIRECT',
    to: string
}

export type ActionClearSigninRedirect = {
    type: 'SIGNIN_CLEAR_REDIRECT'
}

const signinWithTouchID = (token: string): ActionSigninWithTouchID => ({
    type: 'SIGNIN_WITH_TOUCH_ID',
    token
})

const signinDone = (): ActionSigninDone => ({
    type: 'SIGNIN_DONE'
})

const mfaRequired = (mfaToken: string): ActionMfaRequired => ({
    type: 'SIGNIN_MFA_REQUIRED',
    mfaToken
})

const fetchSigninPageData = () =>
    fetchPageData(endpoints.kingdom.signin, 'PAGE_DATA_SIGNIN_RESPONSE')

const resendMail = (queen: string) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        const { login } = getState().signinPageState
        try {
            await ws.get(endpoints.kingdom.activate, {
                params: { email: login, queen: queen }
            })
            dispatch(actionResendMailSuccess())
        } catch (error) {
            console.error('Error resending activation email :', error)
            dispatch(actionResendMailError(error))
        }
    }
}

const signInSuccess = ({ headers, loginType, fromEcommerce }: any) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        const token = headers.authorization
        await dispatch(updateToken(token))
        dispatch(actionSendFormSuccess(token, loginType, fromEcommerce))
        dispatch(getAnalyticsData())
        await dispatch(updateDeliveryRestaurant())
        /** When we signin, add token to WebSocket **/
        // FIXME: No platform specific code in shared
        if (ws.defaults.headers['x-platform'] === 'WEB') {
            sendToken(token)
            getWebsocket()
        }
    }
}

const sendSigninForm = (form: LoginForm, fromEcommerce?: boolean) => {
    return async (
        dispatch: Dispatch<any>,
        getState: GetState
    ): Promise<?{
        message: $Values<typeof messages>,
        mfaToken?: string
    }> => {
        try {
            dispatch(actionSendFormError(undefined, form.login))

            const endpoint = ['FACEBOOK', 'GOOGLE', 'APPLE'].includes(
                form.loginType
            )
                ? endpoints.kingdom.auth.loginWithSocial
                : endpoints.kingdom.auth.loginWithMFA

            const response = await ws.post(endpoint, form)

            if (response) {
                const { data, headers } = response
                if (data && headers) {
                    switch (data.code) {
                        case messages.OK: {
                            dispatch(
                                signInSuccess({
                                    headers,
                                    loginType: form.loginType,
                                    fromEcommerce
                                })
                            )
                            return { message: messages.OK }
                        }
                        case messages.MFA_REQUIRED: {
                            dispatch(mfaRequired(data.mfaToken))
                            return {
                                message: messages.MFA_REQUIRED,
                                mfaToken: data.mfaToken
                            }
                        }

                        case errors.ACCOUNT_INACTIVE:
                        case messages.REDIRECT_SIGNUP:
                            dispatch(
                                socialConnect({
                                    authenticated: false,
                                    token: undefined,
                                    userData: form
                                })
                            )
                            return { message: messages.REDIRECT_SIGNUP }
                        default:
                            break
                    }
                }
            }
        } catch (error) {
            console.warn(error)
            const { response } = error
            let message = 'form.error.default'

            if (error === errors.ACCOUNT_INACTIVE) {
                message = errors.ACCOUNT_INACTIVE
            } else if (response) {
                const { data } = response

                if (data) {
                    const code = data.message || data.code
                    switch (code) {
                        case messages.WRONG_CREDENTIALS:
                        case errors.INVALID_CREDENTIALS:
                            message = 'signin.form.error.invalid.credentials'
                            break
                        case errors.ACCOUNT_INACTIVE:
                            message = errors.ACCOUNT_INACTIVE
                            break
                        case errors.LOGIN_TYPE_INVALID:
                            message = 'signin.form.error.login.type.invalid'
                            break
                        default:
                            break
                    }
                }
            }
            dispatch(actionSendFormError(message, form.login))
        }
    }
}

const actionSendFormSuccess = (
    token: string,
    loginType: 'FACEBOOK' | 'GOOGLE' | 'CLASSIC' | 'APPLE',
    fromEcommerce?: boolean
): ActionSigninSendFormSuccess => ({
    type: 'SIGNIN_SEND_FORM_SUCCESS',
    token,
    loginType,
    fromEcommerce
})

const resetSignin = (error: any): ActionSigninReset => ({
    type: 'SIGNIN_RESET',
    error
})

const actionSendFormError = (
    sendFormError: any,
    login?: string
): ActionSigninSendFormError => ({
    type: 'SIGNIN_SEND_FORM_ERROR',
    sendFormError,
    login
})

const actionResendMailSuccess = (): ActionSigninResendMailSuccess => ({
    type: 'SIGNIN_RESEND_MAIL_SUCCESS'
})

const actionResendMailError = (
    resendMailError: any
): ActionSigninResendMailError => ({
    type: 'SIGNIN_RESEND_MAIL_ERROR',
    resendMailError
})

const socialConnect = (data: SocialData): ActionSignupSocialPending => ({
    type: 'SOCIAL_PENDING',
    data
})

const setSigninRedirect = (to: string): ActionSetSigninRedirect => ({
    to,
    type: 'SIGNIN_SET_REDIRECT'
})

const clearSigninRedirect = (): ActionClearSigninRedirect => ({
    type: 'SIGNIN_CLEAR_REDIRECT'
})

export {
    fetchSigninPageData,
    signInSuccess,
    sendSigninForm,
    resetSignin,
    resendMail,
    signinDone,
    signinWithTouchID,
    setSigninRedirect,
    clearSigninRedirect
}

export type Action =
    | ActionSigninPageDataResponse
    | ActionSigninSendFormError
    | ActionSigninSendFormSuccess
    | ActionSigninResendMailSuccess
    | ActionSigninResendMailError
    | ActionSigninLogout
    | ActionSigninReset
    | ActionSigninWithTouchID
    | ActionsNetwork
    | ActionSetSigninRedirect
    | ActionClearSigninRedirect
    | ActionMfaRequired
